import {
  useSelectedValueByVariableKey,
  useSelectedValueAttributeByKey,
  addCallbacks,
  isNumberVariable,
  useSelectedNumberByVariableKey,
  useVariables,
  useSelections,
  useValues,
  useGroups,
  useAllArticlesAndPrices,
  usePriceFormatter,
} from '@k3/core'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useCellCountAndOffset } from '../three/scene/useCellCountAndOffset'
import { Keys } from '../three/utility/utils'
import SquareFootOutlinedIcon from '@mui/icons-material/SquareFootOutlined'
import OfflineBoltOutlinedIcon from '@mui/icons-material/OfflineBoltOutlined'
import { useDimensions } from '../three/scene/useDimensions'

export const ArticleList = () => {
  const bom = useAllArticlesAndPrices()
  const variables = useVariables()
  const selections = useSelections()
  const values = useValues()
  const groups = useGroups()
  const priceFormatter = usePriceFormatter()

  const { fullThickness, panelDimensions } = useDimensions()
  const { length, width, countWidth, countLength } = useCellCountAndOffset()

  const cellCount = countWidth * countLength
  const qm = (length * width).toFixed(2)

  const cellPower =
    useSelectedNumberByVariableKey(Keys.CellPower.Variable) ?? 5.6

  const CTMAttribute = useSelectedValueAttributeByKey('frontGlassColor', 'CTM')
  const CTMLoss = (100.0 - parseFloat(CTMAttribute?.value as string)) / 100.0

  const product = useSelectedValueByVariableKey(Keys.EFormType.Variable)
  const ausführung = useSelectedValueByVariableKey(Keys.BackGlassVSG.Variable)

  const zellbelegung =
    100 -
    ((cellCount *
      (Math.round(panelDimensions * panelDimensions * 10000) / 10000)) /
      parseFloat(qm)) *
      100

  //This is also used in PDF, careful!
  const articleTableObject = {
    base: [
      {
        title: `${product?.label}<br/>${
          ausführung?.key === 'selected' ? 'VSG <br/>' : ''
        }${fullThickness * 1000} mm Glasdicke`,
        thumbnail: product?.thumbnail,
      },
      {
        title: `${length * 1000} mm x ${width * 1000} mm<br/>${qm} m²${
          product?.key === 'clear'
            ? '<br/>' + zellbelegung.toFixed(0) + '% Transparenzgrad'
            : ''
        }`,
      },
      {
        title: `${cellCount} Zellen<br/>${Math.floor(
          cellCount * cellPower * CTMLoss
        )} Wp nom*`,
      },
    ],
    tableHead: {
      group: '<b>Reiter</b>',
      variable: '<b>Eigenschaft</b>',
      value: '<b>Auswahl</b>',
      price: '<b>Preis</b>',
    },
    tableBody: [
      ...groups
        .map((group) => {
          const sortedVariablesForGroup = variables
            .filter(
              (variable) =>
                variable.groupId === group.id &&
                !(variable.settings.templateOptions?.hideAlways ?? false)
            )
            .sort((variable1, variable2) => variable1.sort - variable2.sort)
          return sortedVariablesForGroup.map((variable, variableIndex) => {
            // Achtung: SUNOVATION unterstützt KEINE Mehrfachauswahlen!
            const selectionForVariable = selections.find(
              (selection) => selection.variableId === variable.id
            )
            const valueForSelection = values.find(
              (value) => value.id === selectionForVariable?.valueId
            )
            const isNumberSelection = isNumberVariable(variable)
            const articleLineForVariable = bom.articleLines
              .filter((ao) => ao.article?.category === variable.key)
              .map((article) => article.priceTotal)
              .reduce((prev, current) => prev + current, 0)
            return {
              group: variableIndex === 0 ? group.label : '',
              variable: variable.label,
              value: isNumberSelection
                ? `${selectionForVariable?.data?.inputValue} mm`
                : valueForSelection!.label,
              price:
                articleLineForVariable > 0
                  ? priceFormatter.format(articleLineForVariable)
                  : '',
            }
          })
        })
        .flat(),
    ],
    tableEnd: {
      group: '<b>Summe</b>',
      variable: '',
      value: '',
      price: `<b>${priceFormatter.format(bom.priceTotal)}</b>`,
    },
    caption: `* Leistungsangaben sind vorläufig`,
    total: priceFormatter.format(bom.priceTotal),
  }

  addCallbacks({
    saveConfiguration: (cfg) => {
      return {
        ...cfg,
        json: {
          ...cfg.json,
          pdfArticleList: articleTableObject,
        },
      }
    },
  })

  console.log(articleTableObject)

  return (
    <Box component="div">
      <Box
        component="div"
        sx={{ display: 'flex', width: '100%', justifyContent: 'space-around' }}
      >
        <Paper
          sx={{
            display: 'flex',
            flexDirection: 'row',
            padding: 2,
            alignItems: 'center',
            justifyContent: 'space-around',
            width: '30%',
          }}
        >
          <img
            src={product?.thumbnail!}
            // srcSet={`${item.img}?w=164&h=164&fit=crop&auto=format&dpr=2 2x`}
            alt={product?.label}
            loading="lazy"
            height="50px"
          />
          <Box
            component="div"
            sx={{
              marginLeft: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'left',
            }}
          >
            <Typography
              className="content"
              dangerouslySetInnerHTML={{
                __html: articleTableObject.base[0].title,
              }}
              variant="body2"
            ></Typography>
          </Box>
        </Paper>

        <Paper
          sx={{
            display: 'flex',
            flexDirection: 'row',
            padding: 2,
            alignItems: 'center',
            justifyContent: 'space-around',
            width: '30%',
          }}
        >
          <SquareFootOutlinedIcon sx={{ height: '50px', width: '50px' }} />
          <Box
            component="div"
            sx={{
              marginLeft: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'left',
            }}
          >
            <Typography
              className="content"
              dangerouslySetInnerHTML={{
                __html: articleTableObject.base[1].title,
              }}
              variant="body2"
            ></Typography>
          </Box>
        </Paper>

        <Paper
          sx={{
            display: 'flex',
            flexDirection: 'row',
            padding: 2,
            alignItems: 'center',
            justifyContent: 'space-around',
            width: '30%',
          }}
        >
          <OfflineBoltOutlinedIcon sx={{ height: '50px', width: '50px' }} />

          <Box
            component="div"
            sx={{
              marginLeft: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'left',
            }}
          >
            <Typography
              className="content"
              dangerouslySetInnerHTML={{
                __html: articleTableObject.base[2].title,
              }}
              variant="body2"
            ></Typography>
          </Box>
        </Paper>
      </Box>
      <br></br>
      <br></br>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label="a dense table">
          <TableHead>
            <InnerTableRow tableObject={articleTableObject.tableHead} />
          </TableHead>

          <TableBody>
            {articleTableObject.tableBody.map((tableRow, i) => {
              return (
                <InnerTableRow
                  key={`inner-table-row_${i}`}
                  tableObject={tableRow}
                />
              )
            })}
            <InnerTableRow tableObject={articleTableObject.tableEnd} />
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption">{articleTableObject.caption}</Typography>
    </Box>
  )
}

const InnerTableRow = (props: {
  tableObject: {
    group: string
    variable: string
    value: string
    price: string
  }
}) => {
  return (
    <TableRow>
      <TableCell
        dangerouslySetInnerHTML={{
          __html: props.tableObject.group,
        }}
      ></TableCell>
      <TableCell
        dangerouslySetInnerHTML={{
          __html: props.tableObject.variable,
        }}
      ></TableCell>
      <TableCell
        dangerouslySetInnerHTML={{
          __html: props.tableObject.value,
        }}
      ></TableCell>
      <TableCell></TableCell>
      <TableCell
        align="right"
        dangerouslySetInnerHTML={{
          __html: props.tableObject.price,
        }}
      ></TableCell>
    </TableRow>
  )
}
